import { Grid } from "@mui/material";
import { useState } from "react";

import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BoltIcon from "@mui/icons-material/Bolt";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleIcon from "@mui/icons-material/People";
import SmsIcon from "@mui/icons-material/Sms";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import BarChartCustom from "../../../../components/shared/chart/BarChartCustom";
import GrowthConversionAnalyticsPanel from "../../components/GrowthConversionAnalyticsPanel";
import {
  DEFAULT_GROWTH_PERIOD_DAYS,
  buildMonthlyEnergyChartData,
  buildSmsMonthlyChartData,
  hasSmsUsageInChart,
} from "../../components/growthAnalyticsConstants";

const CARD_SX = { borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" };

const SystemHealthSection = ({
  analyticsLoading,
  growthAnalytics,
  smsSummary,
  overview,
  periodDays,
  onPeriodChange,
}) => {
  const [monthlyTab, setMonthlyTab] = useState(0);

  const formatRial = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return `${Number(value).toLocaleString("fa-IR")} ریال`;
  };

  const statCards = [
    {
      title: "تعداد کاربران",
      value: (overview?.totalUsers ?? 0).toLocaleString("fa-IR"),
      sub: "کاربر ثبت‌نام شده",
      color: "#1976d2",
      icon: <PeopleIcon />,
    },
    {
      title: "تعداد درخواست‌های نیروگاه",
      value: (overview?.totalRequests ?? 0).toLocaleString("fa-IR"),
      sub: "درخواست ثبت شده",
      color: "#2e7d32",
      icon: <SolarPowerIcon />,
    },
    {
      title: "نیروگاه‌های در حال بهره‌برداری",
      value: (overview?.operatingPlants ?? 0).toLocaleString("fa-IR"),
      sub: "نیروگاه فعال",
      color: "#ed6c02",
      icon: <CheckCircleIcon />,
    },
    {
      title: "تعداد پیمانکاران",
      value: (overview?.activeContractors ?? 0).toLocaleString("fa-IR"),
      sub: "پیمانکار فعال",
      color: "#9c27b0",
      icon: <BusinessIcon />,
    },
    {
      title: "انرژی کل تولید شده",
      value: overview?.hasEnergyData
        ? `${(overview?.totalEnergyProducedKwh ?? 0).toLocaleString("fa-IR")} kWh`
        : "—",
      sub: overview?.hasEnergyData
        ? "از ابتدا تا کنون"
        : "داده تولید انرژی در سامانه ثبت نشده است",
      color: "#d32f2f",
      icon: <BoltIcon />,
    },
  ];

  const smsStatCards = [
    {
      title: "اعتبار باقی‌مانده کاوه‌نگار",
      value: smsSummary?.creditAvailable
        ? formatRial(smsSummary?.remainingCredit)
        : "—",
      sub: smsSummary?.creditAvailable
        ? `نوع حساب: ${smsSummary?.accountType || "—"}`
        : smsSummary?.creditError || "دریافت اعتبار ناموفق",
      color: "#0288d1",
      icon: <AccountBalanceWalletIcon />,
    },
    {
      title: "مصرف پیامک ماه جاری",
      value: smsSummary
        ? `${(smsSummary.monthlySmsCount ?? 0).toLocaleString("fa-IR")} پیام`
        : analyticsLoading
          ? "..."
          : "—",
      sub: smsSummary
        ? `${smsSummary.persianMonthLabel || "ماه جاری"} — ${formatRial(smsSummary.monthlySmsCost)}`
        : "بر اساس لاگ ارسال‌های سامانه",
      color: "#6a1b9a",
      icon: <SmsIcon />,
    },
  ];

  const smsMonthlyChartData = buildSmsMonthlyChartData(smsSummary);
  const smsChartHasUsage = hasSmsUsageInChart(smsSummary);
  const monthlyEnergyData = buildMonthlyEnergyChartData(overview);
  const monthlyEnergyHasData = monthlyEnergyData.some((item) => (item?.انرژی || 0) > 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography fontWeight={700} fontSize={16} sx={{ mb: 1.5 }}>
        سلامت سامانه و سرویس‌ها
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {smsStatCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={4} key={`sms-${idx}`}>
            <Card sx={CARD_SX}>
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Avatar sx={{ bgcolor: card.color, mr: 1, width: 36, height: 36 }}>
                    {card.icon}
                  </Avatar>
                  <Typography fontWeight={700} fontSize={14}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography fontWeight={700} fontSize={20} color={card.color}>
                  {analyticsLoading ? "..." : card.value}
                </Typography>
                <Typography fontSize={13} color="text.secondary">
                  {card.sub}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {statCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={idx >= 3 ? 6 : 4} key={`stat-${idx}`}>
            <Card sx={CARD_SX}>
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Avatar sx={{ bgcolor: card.color, mr: 1, width: 36, height: 36 }}>
                    {card.icon}
                  </Avatar>
                  <Typography fontWeight={700} fontSize={14}>
                    {card.title}
                  </Typography>
                </Box>
                <Typography fontWeight={700} fontSize={20} color={card.color}>
                  {analyticsLoading ? "..." : card.value}
                </Typography>
                <Typography fontSize={13} color="text.secondary">
                  {card.sub}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Card sx={{ ...CARD_SX, height: "100%" }}>
            <CardContent sx={{ py: 1.5, px: 2 }}>
              <GrowthConversionAnalyticsPanel
                analytics={growthAnalytics}
                loading={analyticsLoading}
                height={300}
                periodDays={periodDays}
                onPeriodChange={onPeriodChange}
                showFullAnalyticsLink
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...CARD_SX, height: "100%" }}>
            <CardHeader
              title="مصرف هزینه پیامک (۶ ماه اخیر)"
              sx={{ pb: 0, pt: 1.5, px: 2 }}
              titleTypographyProps={{ fontSize: 15, fontWeight: 600 }}
            />
            <CardContent sx={{ py: 1, px: 2 }}>
              <Box sx={{ height: 280 }}>
                {smsMonthlyChartData.length > 0 ? (
                  <BarChartCustom
                    title="هزینه پیامک‌های ارسالی بر اساس لاگ سامانه"
                    xAxisTitle="ماه"
                    yAxisTitle="هزینه (ریال)"
                    data={smsMonthlyChartData}
                    series={["هزینه", "تعداد"]}
                    colors={["#6a1b9a", "#8e24aa"]}
                    loading={analyticsLoading}
                  />
                ) : (
                  <BarChartCustom
                    title="هزینه پیامک‌های ارسالی بر اساس لاگ سامانه"
                    data={[]}
                    series={["هزینه"]}
                    loading={analyticsLoading}
                  />
                )}
              </Box>
              {!analyticsLoading && smsMonthlyChartData.length > 0 && !smsChartHasUsage ? (
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 1 }}>
                  در ۶ ماه اخیر هزینه پیامکی در لاگ سامانه ثبت نشده است.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...CARD_SX, height: "100%" }}>
            <CardHeader
              title="تحلیل انرژی تولید شده"
              sx={{ pb: 0, pt: 1.5, px: 2 }}
              titleTypographyProps={{ fontSize: 15, fontWeight: 600 }}
            />
            <CardContent sx={{ py: 1, px: 2 }}>
              <Tabs
                value={monthlyTab}
                onChange={(e, v) => setMonthlyTab(v)}
                sx={{ mb: 1, minHeight: 40 }}
              >
                <Tab label="انرژی ماهانه" sx={{ fontSize: 13, minHeight: 40 }} />
              </Tabs>
              <Box sx={{ height: 280 }}>
                <BarChartCustom
                  title="انرژی تولید شده ماهانه"
                  xAxisTitle="ماه"
                  yAxisTitle="انرژی (کیلووات ساعت)"
                  data={monthlyEnergyData}
                  series={["انرژی"]}
                  colors={["#2e7d32"]}
                  loading={analyticsLoading}
                />
              </Box>
              {!analyticsLoading && !monthlyEnergyHasData ? (
                <Typography fontSize={12} color="text.secondary" sx={{ mt: 1 }}>
                  داده تولید انرژی ماهانه در سامانه ثبت نشده است.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemHealthSection;
